import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
	addEdge,
	useNodesState,
	useEdgesState,
	Connection,
	Node,
	ReactFlowProvider,
	useReactFlow,
	MarkerType,
} from '@xyflow/react';
import { Button, Select, Input, Drawer, Form, Checkbox, message } from 'antd';
import { SettingOutlined, PlusOutlined } from '@ant-design/icons';

import Layout from '../../../../components/Layout';
import { DetailDrawer, InlineLoading } from '../../../../components/shared';
import { DeletableEdge, BuilderLayout, BuilderToolbar, FlowCanvas } from '../../../../components/builder';

import { protocolNodeTypes } from './components/ProtocolNodes';
import ProtocolNodePalette from './components/ProtocolNodePalette';
import ProtocolNodePropertiesDrawer from './components/ProtocolNodePropertiesDrawer';

import {
	fetchProtocol,
	createProtocol,
	updateProtocol,
} from '../../../../api/labiq/ProtocalApi';
import { fetchTestCategories } from '../../../../api/healthcare/Testing/TestCategories';
import { fetchTestTypesByCategory } from '../../../../api/healthcare/Testing/TestTypes';
import { fetchClinicCompany } from '../../../../api/admin/companyApi';

import {
	Protocol,
	ProtocolStep,
	ProtocolFlowNode,
	ProtocolFlowEdge,
	ProtocolFlowNodeData,
	ProtocolFlowEdgeType,
	StepType,
	PROTOCOL_NODE_CONFIG,
	ProtocolCanvasData,
	computeProtocolStepNumbers,
	ConditionalNodeConfig,
} from '../../../../types/lab/ProtocolTypes';
import { TestCategory } from '../../../../types/healthcare/TestTypes';
import { Company } from '../../../../types/admin/CompanyTypes';
import { extractValidationErrors } from '../../../../utils/apiError';
import { sampleTypeOptions } from '../../../../types/lab/SampleTypes';

// ── Node counter (module-level for uniqueness) ────────────────────────────────
let nodeCounter = 0;
const genNodeId = (type: StepType) => `${type}_${Date.now()}_${++nodeCounter}`;

// ── Edge defaults ─────────────────────────────────────────────────────────────
const EDGE_STYLE = { strokeWidth: 2, stroke: 'var(--mg-border-strong)' };
const EDGE_DEFAULTS = {
	type: 'deletable',
	style: EDGE_STYLE,
	markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
	animated: false,
};

const edgeTypes = { deletable: DeletableEdge };

// ── Edge helpers ──────────────────────────────────────────────────────────────
const getConnectionType = (sourceHandle?: string | null): ProtocolFlowEdgeType => {
	if (sourceHandle === 'condition_yes') return 'condition_yes';
	if (sourceHandle === 'condition_no') return 'condition_no';
	return 'default';
};

const getEdgeLabel = (ct: ProtocolFlowEdgeType): string | undefined => {
	if (ct === 'condition_yes') return 'Yes';
	if (ct === 'condition_no') return 'No';
	return undefined;
};

// ── Backwards-compat: migrate linear steps → canvas ──────────────────────────
const HORIZONTAL_CENTER = 400;
const VERTICAL_STEP = 160;

function migrateStepsToCanvas(steps: ProtocolStep[]): {
	nodes: ProtocolFlowNode[];
	edges: ProtocolFlowEdge[];
} {
	const sorted = [...steps].sort((a, b) => a.step_number - b.step_number);

	const startId = genNodeId('start');
	const endId = genNodeId('end');

	const startNode: ProtocolFlowNode = {
		id: startId,
		type: 'start',
		position: { x: HORIZONTAL_CENTER, y: 50 },
		data: {
			step_type: 'start',
			label: 'Protocol Start',
			description: '',
			step_number: 1,
		},
	};

	const stepNodes: ProtocolFlowNode[] = sorted.map((step, i) => ({
		id: `step_${step.id ?? i}_${Date.now()}`,
		type: step.step_type,
		position: { x: HORIZONTAL_CENTER, y: 50 + VERTICAL_STEP * (i + 1) },
		data: {
			step_type: step.step_type,
			label: step.name,
			description: step.description ?? '',
			duration_estimate: step.duration_estimate,
			step_number: i + 2, // 1 = start, real steps from 2
			method: step.method ?? [],
			materials: step.materials ?? [],
			configuration: step.configuration ?? {},
			completion_conditions: step.completion_conditions,
			completion_actions: step.completion_actions ?? [],
			failure_actions: step.failure_actions ?? [],
			timeout_actions: step.timeout_actions ?? [],
			timeout_minutes: step.timeout_minutes,
			notes: step.notes,
		},
	}));

	const endNode: ProtocolFlowNode = {
		id: endId,
		type: 'end',
		position: { x: HORIZONTAL_CENTER, y: 50 + VERTICAL_STEP * (sorted.length + 1) },
		data: {
			step_type: 'end',
			label: 'Protocol End',
			description: '',
			step_number: sorted.length + 2,
		},
	};

	const allNodes = [startNode, ...stepNodes, endNode];

	// Linear edges: start → step[0] → step[1] → … → end
	const nodeIds = allNodes.map((n) => n.id);
	const edges: ProtocolFlowEdge[] = nodeIds.slice(0, -1).map((srcId, i) => ({
		id: `edge_${Date.now()}_${i}`,
		source: srcId,
		target: nodeIds[i + 1],
		data: { connection_type: 'default' as ProtocolFlowEdgeType },
		...EDGE_DEFAULTS,
	}));

	return { nodes: allNodes, edges };
}

interface ProtocolFlowBuilderModalProps {
	visible: boolean;
	onClose: () => void;
	protocol?: Protocol;
}

// ── Inner canvas (requires ReactFlow context) ─────────────────────────────────
const ProtocolBuilderCanvas: React.FC<ProtocolFlowBuilderModalProps> = ({ visible, onClose, protocol }) => {
	const { screenToFlowPosition } = useReactFlow();

	// ── Canvas state ──────────────────────────────────────────────────────────
	const [nodes, setNodes, onNodesChange] = useNodesState<ProtocolFlowNode>([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState<ProtocolFlowEdge>([]);

	// ── Selected node / drawer ────────────────────────────────────────────────
	const [selectedNode, setSelectedNode] = useState<ProtocolFlowNode | null>(null);
	const [drawerOpen, setDrawerOpen] = useState(false);

	// ── Protocol metadata ─────────────────────────────────────────────────────
	const [protocolName, setProtocolName] = useState('');
	const [protocolDescription, setProtocolDescription] = useState('');
	const [protocolVersion, setProtocolVersion] = useState('1.0');
	const [isActive, setIsActive] = useState(true);
	const [isFirstProtocol, setIsFirstProtocol] = useState(false);
	const [testTypes, setTestTypes] = useState<string[]>([]);
	const [sampleTypes, setSampleTypes] = useState<string[]>([]);
	const [company, setCompany] = useState<Company | null>(null);

	// ── Settings drawer ───────────────────────────────────────────────────────
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [testCategories, setTestCategories] = useState<TestCategory[]>([]);
	const [selectedCategory, setSelectedCategory] = useState<TestCategory | undefined>();
	const [availableTestTypes, setAvailableTestTypes] = useState<string[]>([]);

	// ── Loading / saving ──────────────────────────────────────────────────────
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	// ── Load company & test categories on mount ───────────────────────────────
	useEffect(() => {
		fetchClinicCompany()
			.then((comp) => setCompany(comp))
			.catch(() => { });

		fetchTestCategories()
			.then((res) => {
				const cats = Array.isArray((res as any).data) ? (res as any).data : [];
				setTestCategories(cats);
			})
			.catch(() => { });
	}, []);

	// ── Load test types when category changes ────────────────────────────────
	useEffect(() => {
		if (!selectedCategory) { setAvailableTestTypes([]); return; }
		fetchTestTypesByCategory(selectedCategory.value, company?.id)
			.then((res) => {
				const types = Array.isArray((res as any).data) ? (res as any).data : [];
				setAvailableTestTypes(types.map((t: any) => t.value ?? t.name ?? t));
			})
			.catch(() => setAvailableTestTypes([]));
	}, [selectedCategory?.value, company?.id]);

	// ── Load protocol when editing ────────────────────────────────────────────
	useEffect(() => {
		if (!protocol || protocol.id === 0) {
			// New protocol: start with a single Start node
			const startId = genNodeId('start');
			setNodes([{
				id: startId,
				type: 'start',
				position: { x: HORIZONTAL_CENTER, y: 200 },
				data: { step_type: 'start', label: 'Protocol Start', description: '', step_number: 1 },
			}] as any);
			return;
		}

		setLoading(true);
		fetchProtocol(Number(protocol.id))
			.then((protocol) => {
				setProtocolName(protocol.name);
				setProtocolDescription(protocol.description ?? '');
				setProtocolVersion(protocol.version);
				setIsActive(protocol.is_active);
				setIsFirstProtocol(protocol.is_first_protocol);
				setTestTypes(protocol.test_types ?? []);
				setSampleTypes(protocol.sample_types ?? []);

				if (protocol.canvas_data) {
					// Restore canvas from saved data
					const savedNodes = protocol.canvas_data.nodes as ProtocolFlowNode[];
					const savedEdges = protocol.canvas_data.edges as ProtocolFlowEdge[];
					const stepNums = computeProtocolStepNumbers(savedNodes, savedEdges);
					const annotated = savedNodes.map((n) => ({
						...n,
						data: { ...n.data, step_number: stepNums.get(n.id) ?? n.data.step_number ?? 1 },
					}));
					setNodes(annotated as any);
					setEdges(savedEdges.map((e) => ({ ...e, type: 'deletable' })) as any);
				} else if (protocol.steps?.length) {
					// Legacy protocol: generate canvas from linear steps
					const { nodes: migrated, edges: migratedEdges } = migrateStepsToCanvas(protocol.steps);
					const stepNums = computeProtocolStepNumbers(migrated, migratedEdges);
					const annotated = migrated.map((n) => ({
						...n,
						data: { ...n.data, step_number: stepNums.get(n.id) ?? n.data.step_number },
					}));
					setNodes(annotated as any);
					setEdges(migratedEdges as any);
				} else {
					// No steps: start node only
					const startId = genNodeId('start');
					setNodes([{
						id: startId,
						type: 'start',
						position: { x: HORIZONTAL_CENTER, y: 200 },
						data: { step_type: 'start', label: 'Protocol Start', description: '', step_number: 1 },
					}] as any);
				}
			})
			.catch(() => message.error('Failed to load protocol'))
			.finally(() => setLoading(false));
	}, [protocol?.id]);

	// ── Drag-and-drop ─────────────────────────────────────────────────────────
	const onDrop = useCallback(
		(event: React.DragEvent) => {
			event.preventDefault();
			const stepType = event.dataTransfer.getData('application/protocol-node') as StepType;
			if (!stepType) return;

			const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
			const cfg = PROTOCOL_NODE_CONFIG[stepType];
			const newNode: ProtocolFlowNode = {
				id: genNodeId(stepType),
				type: stepType,
				position,
				data: {
					step_type: stepType,
					label: cfg.label,
					description: '',
					duration_estimate: stepType !== 'start' && stepType !== 'end' && stepType !== 'conditional' ? 60 : undefined,
					method: [],
					materials: [],
					configuration: { required_fields: [], equipment_requirements: [] },
					completion_actions: [],
					failure_actions: [],
					timeout_actions: [],
					// Initialise conditional_config for conditional nodes
					...(stepType === 'conditional' ? {
						conditional_config: { group_operator: 'AND', conditions: [] } satisfies ConditionalNodeConfig,
					} : {}),
				},
			};
			setNodes((nds) => nds.concat(newNode as any));
		},
		[screenToFlowPosition],
	);

	const onDragOver = useCallback((event: React.DragEvent) => {
		event.preventDefault();
		event.dataTransfer.dropEffect = 'move';
	}, []);

	const onPaletteDragStart = (event: React.DragEvent, stepType: StepType) => {
		event.dataTransfer.setData('application/protocol-node', stepType);
		event.dataTransfer.effectAllowed = 'move';
	};

	// ── Connect nodes ─────────────────────────────────────────────────────────
	const onConnect = useCallback(
		(connection: Connection) => {
			const connectionType = getConnectionType(connection.sourceHandle);
			const label = getEdgeLabel(connectionType);
			const edge: ProtocolFlowEdge = {
				id: `edge_${Date.now()}`,
				source: connection.source!,
				target: connection.target!,
				sourceHandle: connection.sourceHandle ?? undefined,
				targetHandle: connection.targetHandle ?? undefined,
				label,
				data: { connection_type: connectionType },
				...EDGE_DEFAULTS,
			};
			setEdges((eds) => addEdge(edge as any, eds));
		},
		[setEdges],
	);

	// ── Node click / pane click ───────────────────────────────────────────────
	const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
		setSelectedNode(node as ProtocolFlowNode);
		setDrawerOpen(true);
	}, []);

	const onPaneClick = useCallback(() => {
		setSelectedNode(null);
		setDrawerOpen(false);
	}, []);

	// ── Node data updates ─────────────────────────────────────────────────────
	const handleNodeUpdate = useCallback(
		(nodeId: string, updates: Partial<ProtocolFlowNodeData>) => {
			setNodes((nds) =>
				nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...updates } } : n)),
			);
			setSelectedNode((prev) =>
				prev?.id === nodeId ? { ...prev, data: { ...prev.data, ...updates } } : prev,
			);
		},
		[],
	);

	const handleNodeDelete = useCallback(
		(nodeId: string) => {
			setNodes((nds) => nds.filter((n) => n.id !== nodeId));
			setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
			setSelectedNode(null);
			setDrawerOpen(false);
		},
		[],
	);

	// ── Build save payload ────────────────────────────────────────────────────
	const buildSavePayload = useCallback(
		(currentNodes: ProtocolFlowNode[], currentEdges: ProtocolFlowEdge[]): Partial<Protocol> => {
			const stepNums = computeProtocolStepNumbers(currentNodes, currentEdges);

			// Annotate nodes with their computed step numbers
			const annotated: ProtocolFlowNode[] = currentNodes.map((n) => ({
				...n,
				data: { ...n.data, step_number: stepNums.get(n.id) ?? 1 },
			}));

			// Build the steps array (includes all node types: start, end, conditional, lab steps)
			const steps: Partial<ProtocolStep>[] = annotated
				.sort((a, b) => (a.data.step_number ?? 0) - (b.data.step_number ?? 0))
				.map((n) => {
					const outgoingEdges = currentEdges.filter((e) => e.source === n.id);
					const nextStepRules = outgoingEdges.map((e) => {
						const targetStepNum = stepNums.get(e.target) ?? 0;
						return {
							next_step: targetStepNum,
							connection_type: e.data?.connection_type ?? 'default',
						};
					});

					// Resolve step_number_ref in each conditional condition so the
					// backend execution engine can route without loading canvas_data.
					let resolvedConditionalConfig: ConditionalNodeConfig | undefined;
					if (n.data.step_type === 'conditional' && n.data.conditional_config) {
						const raw = n.data.conditional_config as ConditionalNodeConfig;
						resolvedConditionalConfig = {
							...raw,
							conditions: raw.conditions.map((c) => ({
								...c,
								step_number_ref: c.step_node_id
									? (stepNums.get(c.step_node_id) ?? c.step_number_ref)
									: c.step_number_ref,
							})),
						};
					}

					return {
						// Use existing step id if node was migrated from a real step
						step_number: n.data.step_number ?? 1,
						step_type: n.data.step_type,
						name: n.data.label || n.data.step_type,
						description: n.data.description,
						duration_estimate: n.data.duration_estimate ?? 0,
						method: n.data.method ?? [],
						materials: n.data.materials ?? [],
						configuration: {
							...(n.data.configuration ?? {}),
							next_steps: nextStepRules,
							// Embed resolved conditional config so backend can evaluate without canvas_data
							...(resolvedConditionalConfig ? { conditional_config: resolvedConditionalConfig } : {}),
						},
						completion_conditions: n.data.completion_conditions,
						completion_actions: n.data.completion_actions ?? [],
						failure_actions: n.data.failure_actions ?? [],
						timeout_actions: n.data.timeout_actions ?? [],
						timeout_minutes: n.data.timeout_minutes,
						notes: n.data.notes,
					};
				});

			const canvasData: ProtocolCanvasData = {
				nodes: annotated,
				edges: currentEdges,
			};

			return {
				name: protocolName,
				description: protocolDescription,
				version: protocolVersion,
				is_active: isActive,
				is_first_protocol: isFirstProtocol,
				test_types: testTypes,
				sample_types: sampleTypes,
				steps: steps as ProtocolStep[],
				canvas_data: canvasData,
			};
		},
		[
			protocolName, protocolDescription, protocolVersion, isActive,
			isFirstProtocol, testTypes, sampleTypes,
		],
	);

	// ── Save ──────────────────────────────────────────────────────────────────
	const handleSave = useCallback(async () => {
		if (!protocolName.trim()) {
			message.warning('Please enter a protocol name');
			return;
		}
		if (!protocolVersion.trim()) {
			message.warning('Please enter a version number');
			return;
		}

		setSaving(true);
		try {
			const payload = buildSavePayload(nodes as ProtocolFlowNode[], edges as ProtocolFlowEdge[]);

			if (protocol && protocol.id !== 0) {
				await updateProtocol(Number(protocol.id), payload);
				message.success('Protocol saved');
			} else {
				const created = await createProtocol(payload);
				message.success('Protocol created');
			}
		} catch (err: any) {
			message.error(extractValidationErrors(err).join(' '));
		} finally {
			setSaving(false);
		}
	}, [nodes, edges, buildSavePayload, protocol]);

	// ── MiniMap colour ────────────────────────────────────────────────────────
	const miniMapColor = useCallback(
		(node: Node) => PROTOCOL_NODE_CONFIG[(node.data as ProtocolFlowNodeData).step_type]?.color ?? '#94a3b8',
		[],
	);

	// ── Toolbar extra slot ────────────────────────────────────────────────────
	const toolbarExtra = (
		<>
			<Input
				value={protocolVersion}
				onChange={(e) => setProtocolVersion(e.target.value)}
				placeholder="1.0"
				style={{ width: 80 }}
				addonBefore="v"
			/>
			<Select
				value={isActive ? 'active' : 'inactive'}
				onChange={(v) => setIsActive(v === 'active')}
				style={{ width: 120 }}
				options={[
					{ value: 'active', label: '● Active' },
					{ value: 'inactive', label: '○ Inactive' },
				]}
			/>
			<Button icon={<SettingOutlined />} onClick={() => setSettingsOpen(true)}>
				Settings
			</Button>
		</>
	);

	// ── Empty canvas hint ─────────────────────────────────────────────────────
	const emptyHint = (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				color: 'var(--mg-text-disabled)',
				pointerEvents: 'none',
			}}
		>
			<PlusOutlined style={{ fontSize: 40, marginBottom: 12 }} />
			<div style={{ fontSize: 16, fontWeight: 500 }}>Drag steps from the left panel</div>
			<div style={{ fontSize: 13, marginTop: 4 }}>Start with a Start node, then add lab steps</div>
		</div>
	);

	return (
		<DetailDrawer
			open={visible}
			onClose={onClose}
			width="85vw"
			headerActions={
				<BuilderToolbar
					// onBack={onClose}
					name={protocolName}
					onNameChange={setProtocolName}
					namePlaceholder="Protocol name…"
					description={protocolDescription}
					onDescriptionChange={setProtocolDescription}
					extraSlot={toolbarExtra}
					onSave={handleSave}
					saving={saving}
				/>
			}
			noPadding
		>
			{loading ? (
				<InlineLoading minHeight={400} message="Loading protocol…" />
			) : (
				<>
					<BuilderLayout
						paletteWidth={210}
						palette={<ProtocolNodePalette onDragStart={onPaletteDragStart} />}
					>
						<FlowCanvas
							nodes={nodes}
							edges={edges}
							nodeTypes={protocolNodeTypes as any}
							edgeTypes={edgeTypes}
							onNodesChange={onNodesChange}
							onEdgesChange={onEdgesChange}
							onConnect={onConnect}
							onNodeClick={onNodeClick}
							onPaneClick={onPaneClick}
							onDrop={onDrop}
							onDragOver={onDragOver}
							defaultEdgeOptions={EDGE_DEFAULTS as any}
							miniMapNodeColor={miniMapColor}
							emptyState={emptyHint}
						/>

						{/* Node properties drawer */}
						<ProtocolNodePropertiesDrawer
							node={selectedNode}
							open={drawerOpen}
							onClose={() => { setDrawerOpen(false); setSelectedNode(null); }}
							onUpdate={handleNodeUpdate}
							onDelete={handleNodeDelete}
							allNodes={nodes as ProtocolFlowNode[]}
							allEdges={edges as ProtocolFlowEdge[]}
							companyId={company?.id}
							companyName={company?.name}
							protocol={{
								name: protocolName,
								version: protocolVersion,
								is_active: isActive,
								is_first_protocol: isFirstProtocol,
								test_types: testTypes,
								sample_types: sampleTypes,
							}}
						/>

						{/* Protocol settings drawer */}
						<Drawer
							title="Protocol Settings"
							placement="right"
							width={420}
							open={settingsOpen}
							onClose={() => setSettingsOpen(false)}
							styles={{ body: { padding: '16px 24px' } }}
						>
							<Form layout="vertical" size="small">
								<Form.Item label="Is First Protocol">
									<Checkbox
										checked={isFirstProtocol}
										onChange={(e) => setIsFirstProtocol(e.target.checked)}
									>
										This is the first protocol in a sequence (no preceding test required)
									</Checkbox>
								</Form.Item>

								{!isFirstProtocol && (
									<>
										<Form.Item label="Testing Category">
											<Select
												value={selectedCategory?.value}
												onChange={(val) => {
													const cat = testCategories.find((c) => c.value === val);
													setSelectedCategory(cat);
													setTestTypes([]);
												}}
												placeholder="Select category"
												allowClear
												options={testCategories.map((c) => ({ value: c.value, label: c.name }))}
											/>
										</Form.Item>

										<Form.Item label="Applicable Test Types">
											<Select
												mode="multiple"
												value={testTypes}
												onChange={setTestTypes}
												placeholder={selectedCategory ? 'Select test types' : 'Select a category first'}
												disabled={!selectedCategory}
												options={availableTestTypes.map((t) => ({ value: t, label: t }))}
											/>
										</Form.Item>
									</>
								)}

								<Form.Item label="Applicable Sample Types">
									<Select
										mode="multiple"
										value={sampleTypes}
										onChange={setSampleTypes}
										options={sampleTypeOptions}
										placeholder="Select sample types"
									/>
								</Form.Item>
							</Form>
						</Drawer>
					</BuilderLayout>
				</>
			)}
		</DetailDrawer>
	);
};

interface ProtocolFlowBuilderModalProps {
	visible: boolean;
	onClose: () => void;
	protocol?: Protocol;
}

// ── Wrapper: provide ReactFlow context ────────────────────────────────────────
const ProtocolFlowBuilderModal: React.FC<ProtocolFlowBuilderModalProps> = ({ visible, onClose, protocol }) => (
	<ReactFlowProvider>
		<ProtocolBuilderCanvas visible={visible} onClose={onClose} protocol={protocol} />
	</ReactFlowProvider>
);

export default ProtocolFlowBuilderModal;
